import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

// TS type
import { TTripType } from '@redux/common';
// Redux
import { RootState } from '@redux/store';

// import module
import CyRcnn from '@magaele/cy_rcnn';
import NvbRslb from '@magaele/nvb_rslb';
import SlideFooter from '@Common/SlideFooter';
import StRcln from '@magaele/st_rcln';

// component
import MobileHeader from '@Common/mobileHeader';

// source config

// styles
import './style.scss';

// common function
import { getTHSRTripInfo } from '@libs/core';
import { calendarLoc } from '@libs/globalVariables';

// plugin
import dayjs from 'dayjs';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

interface ICalendarMProps {
    disabled: Boolean;
    error: Boolean;
    type?: string; //控制來回 or 單趟
    THSRTripType: string;
    whenDatesChange: (data: any) => void;
}

const CalendarM: React.FC<ICalendarMProps> = (props) => {
    const { THSRTripType } = props;
    const { t } = useTranslation();

    const { Language } = useSelector((state: RootState) => state.Common);
    let lang: string;
    // 香港語系翻譯檔與台灣相同
    Language === 'zh-hk' ? lang = 'zh-tw' : lang = Language;
    const store_THSR = useSelector((state: RootState) => state.THSR);
    const currentThsrTripInfo = getTHSRTripInfo(store_THSR.THSRInitData.ThsrTripInfos, THSRTripType)

    const [visible, setVisible] = useState<boolean>(false);
    const [activeInput, setActiveInput] = useState<number>(null);
    const [selectedStartDate, setSelectedStartDate] = useState<string>(
        currentThsrTripInfo.StartDate
            ? dayjs(currentThsrTripInfo.StartDate).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD')
    );
    const [selectedEndDate, setSelectedEndDate] = useState<string>(
        currentThsrTripInfo.StartDate
            ? dayjs(currentThsrTripInfo.StartDate).add(1, 'days').format('YYYY-MM-DD')
            : dayjs().add(1, 'days').format('YYYY-MM-DD')
    );
    const timesArr = store_THSR.THSRInitData.EstimateDepartTimes.map((child) => ({
        value: child.DeptTimeValue,
        text: child.DeptTimeText,
    }));

    const [selectedStartTime, setSelectedStartTime] = useState<string>(
        timesArr.some((item) => item.value === '0500') ? '0500' : timesArr[0].value
    );
    const [selectedEndTime, setSelectedEndTime] = useState<string>(
        timesArr.some((item) => item.value === '0500') ? '0500' : timesArr[0].value
    );

    const calendarlang = {
        year: lang === 'zh-tw' ? '年' : ' ',
        month: calendarLoc[lang].months,
        date: calendarLoc[lang].weekdaysForCyRcnn,
    };

    const whenDateClick = (order, date) => {
        switch (order) {
            case 0:
                setSelectedStartDate(date);
                setSelectedEndDate('');
                break;
            case 1:
                setSelectedEndDate(date);
                break;
            default:
                if (selectedStartDate && selectedEndDate) {
                    setSelectedStartDate(date);
                    setSelectedEndDate('');
                }
                break;
        }
    };

    const whenClickConfirm = () => {
        setVisible(false);
        /*
            TODO:
            1.次日最遠可定日期API
            2.查詢空位班次API
        */
        props.whenDatesChange({
            startDate: selectedStartDate,
            endDate: THSRTripType === TTripType.round ? selectedEndDate : '',
            startTime: selectedStartTime,
            endTime: THSRTripType === TTripType.round ? selectedEndTime : '',
        });
    };
    const formatTime = (time) => {
        return dayjs(dayjs().format('YYYY/MM/DD') + ' ' + time).format('HH:mm');
    };
    const calcStartDate = () => {
        return currentThsrTripInfo.StartDate
            ? dayjs(currentThsrTripInfo.StartDate).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD');
    };
    const calcEndDate = () => {
        if (selectedStartDate && !selectedEndDate && THSRTripType === TTripType.round) {
            if (
                currentThsrTripInfo.EndDate &&
                dayjs(selectedStartDate)
                    .add(store_THSR.THSRInitData.MaxReturnDays, 'days')
                    .isAfter(currentThsrTripInfo.EndDate)
            ) {
                return dayjs(currentThsrTripInfo.EndDate).format('YYYY-MM-DD');
            } else {
                return dayjs(selectedStartDate)
                    .add(store_THSR.THSRInitData.MaxReturnDays || 1, 'days')
                    .format('YYYY-MM-DD');
            }
        } else {
            if (currentThsrTripInfo.EndDate) {
                return dayjs(currentThsrTripInfo.EndDate).format('YYYY-MM-DD');
            } else {
                //當init API沒有給endDate
                return dayjs().add(1, 'month').format('YYYY-MM-DD');
            }
        }
    };

    const calcHighlightDays = () => {
        if (!store_THSR.THSRHighestDiscounts || store_THSR.THSRHighestDiscounts.length === 0)
            return [];
        let highLightDays = [];

        store_THSR.THSRHighestDiscounts.forEach((element) => {
            let day = dayjs(element.Date).format('YYYY-MM-DD');
            if (!highLightDays.includes(day)) {
                highLightDays.push(day);
            }
        });
        return highLightDays;
    };

    // 月曆提示文字的最高折扣數
    const currentHighestDiscount = () => {
        if (!store_THSR?.THSRInitData) return 0;
        let currentThsrTripInfo = store_THSR.THSRInitData.ThsrTripInfos.find(
            (child) => child.ThsrType === THSRTripType
        );
        return currentThsrTripInfo ? currentThsrTripInfo.HighestDiscountOff : 0;
    };

    useEffect(() => {
        props.whenDatesChange({
            startDate: selectedStartDate,
            endDate: THSRTripType === TTripType.round ? selectedEndDate : '',
            startTime: selectedStartTime,
            endTime: THSRTripType === TTripType.round ? selectedEndTime : '',
        });
    }, [THSRTripType, selectedStartDate, selectedEndDate, selectedStartTime, selectedEndTime]);

    return (
        <div className="calendarM">
            <div
                className={cx('dateTrigger', {
                    error: props.error,
                    disabled: props.disabled,
                })}
                onClick={() => {
                    if (!props.disabled) {
                        setVisible(true);
                        setActiveInput(0);
                    }
                }}
            >
                <div className="placeHolder">
                    <span className="label">
                        {t('THSR.commonPanel.date_departure', { symbol: ':' })}
                    </span>
                    <br />
                    <span className={cx({ error: props.error })}>
                        {selectedStartDate}, {formatTime(selectedStartTime)}
                    </span>
                </div>
            </div>
            {THSRTripType === TTripType.round ? (
                <div
                    className={cx('dateTrigger', {
                        error: props.error,
                        disabled: props.disabled,
                    })}
                    onClick={() => {
                        if (!props.disabled) {
                            setVisible(true);
                            setActiveInput(1);
                        }
                    }}
                >
                    <div className="placeHolder">
                        <span className="label">
                            {t('THSR.commonPanel.date_end', { symbol: ':' })}
                        </span>
                        <br />
                        <span className={cx({ error: props.error })}>
                            {selectedEndDate}, {formatTime(selectedEndTime)}
                        </span>
                    </div>
                </div>
            ) : null}
            <NvbRslb className="calendarM" visible={visible} direction="right">
                <MobileHeader
                    title={t('THSR.commonPanel.choose_date')}
                    onLeftIconClick={() => {
                        setVisible(false);
                        whenClickConfirm();
                    }}
                />
                {visible && (
                    <CyRcnn
                        doubleChoose={THSRTripType === TTripType.round}
                        selectedStartDate={selectedStartDate}
                        selectedEndDate={THSRTripType === TTripType.round ? selectedEndDate : ''}
                        activeInput={activeInput}
                        startDate={calcStartDate()}
                        endDate={calcEndDate()}
                        startMonth={
                            currentThsrTripInfo.StartDate
                                ? dayjs(currentThsrTripInfo.StartDate).format('YYYY-MM')
                                : dayjs().format('YYYY-MM')
                        }
                        endMonth={
                            currentThsrTripInfo.EndDate
                                ? dayjs(currentThsrTripInfo.EndDate).format('YYYY-MM')
                                : dayjs().add(1, 'month').format('YYYY-MM')
                        }
                        startLabelTitle={t('THSR.commonPanel.date_departure')}
                        endLabelTitle={t('THSR.commonPanel.date_destination')}
                        // ref={(e) => {
                        //     this.calendar = e;
                        // }}
                        language={calendarlang}
                        onDateClickCallBack={(order: any, date: string) => {
                            whenDateClick(order, date);
                        }}
                        onClickConfirm={() => setVisible(false)}
                        customDiffTxt={(diffDate) => {
                            const showTxt = diffDate + 1;
                            return t('THSR.commonPanel.all') + showTxt + t('THSR.commonPanel.day');
                        }}
                        device="M"
                        startTxt={t('THSR.commonPanel.departure')}
                        endTxt={t('THSR.commonPanel.returnTrip')}
                        highLightDaysArr={calcHighlightDays()}
                    />
                )}

                <div className="timesSelect">
                    {store_THSR.THSRHighestDiscounts &&
                        store_THSR.THSRHighestDiscounts.length > 0 && (
                            <ul className="eventList">
                                <li className="eventItem">
                                    {t('THSR.commonPanel.discountDateHintMsg', {
                                        discount:
                                            Language === 'zh-tw' || 'zh-hk' || 'zh-ch' || 'ko-kr'
                                                ? 100 - currentHighestDiscount()
                                                : currentHighestDiscount(),
                                    })}     
                                </li>
                            </ul>
                        )}

                    <StRcln
                        option={timesArr}
                        placeholder="請選擇"
                        label={t('THSR.commonPanel.outboundTrip')}
                        // whenClick={() => console.log('父層whenClick')}
                        defaultValue={selectedStartTime}
                        breakline={true}
                        onChangeCallBack={(v) => {
                            setSelectedStartTime(v);
                        }}
                        dropdownContentClassName={'THSR-dropdown'}
                    ></StRcln>
                    {THSRTripType === TTripType.round ? (
                        <StRcln
                            option={timesArr}
                            placeholder="請選擇"
                            label={t('THSR.commonPanel.returnTrip')}
                            // whenClick={() => console.log('父層whenClick')}
                            defaultValue={selectedEndTime}
                            breakline={true}
                            onChangeCallBack={(v) => {
                                setSelectedEndTime(v);
                            }}
                            dropdownContentClassName={'THSR-dropdown'}
                        ></StRcln>
                    ) : null}
                </div>
                <SlideFooter
                    hide={false}
                    onlyRightBtn={true}
                    onClickLeftBtn={() => {
                        console.log('點點左邊');
                    }}
                    onClickRightBtn={() => whenClickConfirm()}
                    leftBtnText={t('THSR.commonPanel.cancel')}
                    rightBtnText={t('THSR.commonPanel.confirm')}
                />
            </NvbRslb>
        </div>
    );
};

export default CalendarM;
